/**
 * ZIP Code Validation Hook
 *
 * Provides real-time ZIP code formatting.
 * Formats to XXXXX or XXXXX-XXXX pattern (5-digit or ZIP+4).
 *
 * Validation is handled by Zod schemas (see src/schemas/orderFormSchema.ts)
 * This hook focuses only on formatting for better UX
 *
 * Usage:
 * ```tsx
 * const { format, inputProps } = useZipValidation();
 *
 * <input
 *   {...register('zipCode')}
 *   {...inputProps}
 *   onChange={(e) => {
 *     const formatted = format(e.target.value);
 *     setValue('zipCode', formatted);
 *   }}
 * />
 * ```
 */

import { useCallback } from 'react';

interface UseZipValidationResult {
  /**
   * Format a ZIP code as the user types
   * Returns formatted string: 93401 or 93401-1234
   */
  format: (value: string) => string;

  /**
   * Mobile-optimized input props for ZIP code fields
   * Includes type, inputMode, autoComplete, and pattern
   */
  inputProps: {
    type: 'text';
    inputMode: 'numeric';
    autoComplete: 'postal-code';
    pattern: '[0-9-]*';
  };
}

/**
 * Hook for ZIP code formatting
 * Supports both 5-digit (93401) and ZIP+4 (93401-1234) formats
 *
 * Note: Validation is handled by Zod zipCodeSchema in orderFormSchema.ts
 */
export function useZipValidation(): UseZipValidationResult {
  /**
   * Format ZIP code as user types
   * Transforms input into 93401 or 93401-1234 format
   */
  const format = useCallback((value: string): string => {
    if (!value) return '';

    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');

    // Limit to 9 digits (5 + 4 for ZIP+4)
    const limitedDigits = digits.slice(0, 9);

    // Format based on number of digits
    if (limitedDigits.length <= 5) {
      return limitedDigits;
    } else {
      // Add hyphen for ZIP+4
      return `${limitedDigits.slice(0, 5)}-${limitedDigits.slice(5)}`;
    }
  }, []);

  // Mobile-optimized input props
  const inputProps = {
    type: 'text' as const, // Not 'number' - causes issues with leading zeros
    inputMode: 'numeric' as const,
    autoComplete: 'postal-code' as const,
    pattern: '[0-9-]*' as const,
  };

  return {
    format,
    inputProps,
  };
}

/**
 * Extract raw digits from a formatted ZIP code
 * Useful for submitting to backend APIs
 *
 * @param formatted - Formatted ZIP code: 93401-1234
 * @returns Raw digits: 934011234
 */
export function extractZipDigits(formatted: string): string {
  return formatted.replace(/\D/g, '');
}

/**
 * Check if a value looks like a ZIP code
 * Useful for conditional validation
 *
 * @param value - String to check
 * @returns True if the string contains ZIP-like digits (5+ digits)
 */
export function looksLikeZip(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  return digits.length >= 5;
}
